import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem } from '@creditornot/cb-menu';
import { ModalHeader, ModalContent } from '@creditornot/cb-modal';
import { Popover } from '@creditornot/cb-popover';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { getAnnualReportCsv } from 'apis/merchant-payout-service/api';
import { getAnnualReportCsvLink } from 'apis/restaurant-api/merchant-admin/payouts/api';
import { WoltModal } from 'common/components';
import PageHeader from 'common/components/PageHeader';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { downloadFileFromAxiosResponse, downloadFromUrl } from 'common/utils/download';
import { usePermissions, hasPermissions } from 'common/wolt-permissions';
import { useVenueContext } from 'modules/venue/VenueContext';

import InvoiceList from './InvoiceList';
import PayoutReportList from './PayoutReportList';
import { reportAnnualReportCsvDownload } from './behavioral';

const Root = styled.div`
  /* Add space to the bottom so user can scroll the content past the Intercom bubble. */
  padding-block-end: 80px;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const SegmentedControlContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  gap: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const StyledWoltModal = styled(WoltModal)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  padding: 0;
  width: 500px;
`;

enum AnnualReportDownloadStatus {
  Initial,
  Downloading,
  Error,
}

const AnnualReportGenerationsModal = (props: {
  isOpen: boolean;
  status?: AnnualReportDownloadStatus;
  onCloseClick: () => void;
}) => (
  <StyledWoltModal isOpen={props.isOpen} onRequestClose={props.onCloseClick}>
    <ModalHeader onCloseClick={props.onCloseClick}>
      <FormattedMessage id="payout-reports.annual_report_modal_header" />
    </ModalHeader>
    <ModalContent>
      {props.status === AnnualReportDownloadStatus.Downloading && (
        <Alert variant="success">
          <FormattedMessage id="payout-reports.annual_report_modal_content_downloading" />
        </Alert>
      )}
      {props.status === AnnualReportDownloadStatus.Error && (
        <Alert variant="error">
          <FormattedMessage id="payout-reports.annual_report_modal_content_error" />
        </Alert>
      )}
    </ModalContent>
  </StyledWoltModal>
);

const annualReportOptions = [...Array(new Date().getFullYear() - 2020)].map((_, i) => 2020 + i); // We can get annual reports only 2020 and after

const OPS_TOOLS_DEV_URL = 'https://ops-tools.development.dev.woltapi.com';
const OPS_TOOLS_PROD_URL = 'https://ops.wolt.com';
const isProdEnv = process.env.APP_ENV === 'production';

const PayoutReportPage = () => {
  const venue = useVenueContext();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [reportsOrInvoices, setReportsOrInvoices] = useState<'reports' | 'invoices'>('reports');
  const [annualReportDownloadStatus, setAnnualReportDownloadStatus] = useState<AnnualReportDownloadStatus>(
    AnnualReportDownloadStatus.Initial,
  );

  /*
   * Download annual report from Rapi. This is a fallback solution for venues that don't have proper json data. It's
   * very difficult to fix everything in MPS. So we have this solution to download the annual report from Rapi for
   * from 2020 & 2021.
   */
  const handleAnnualReportFromRapi = async (year: number) => {
    setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Downloading);

    try {
      const url = await getAnnualReportCsvLink(venue.id, year);
      downloadFromUrl(url.data);
      reportAnnualReportCsvDownload();
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Initial);
    } catch (e) {
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Error);
    }
  };

  const handleAnnualReportCsv = async (year: number) => {
    try {
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Downloading);
      const response = await getAnnualReportCsv(venue.id, year);
      downloadFileFromAxiosResponse(response);
      reportAnnualReportCsvDownload();
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Initial);
    } catch (_) {
      setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Error);
    }
  };

  const handleReportOrInvoice = (value: string) => {
    setReportsOrInvoices(value as 'reports' | 'invoices');
  };
  const permissions = usePermissions();

  const isPayoutsDeveloper = hasPermissions(
    [{ resource: 'payouts.maintenance', operations: ['c', 'r', 'u', 'd'] }],
    permissions,
  );

  return (
    <Root>
      <PageHeader title={intl.formatMessage({ id: 'navigation.payout-reports' })}>
        <Popover
          placement="bottom-start"
          showOnHover={false}
          renderArrow={false}
          content={
            <div>
              {annualReportOptions.map(year => (
                <MenuActionItem
                  variant="blue"
                  key={year}
                  onClick={() => (year <= 2021 ? handleAnnualReportFromRapi(year) : handleAnnualReportCsv(year))}
                >
                  {year}
                </MenuActionItem>
              ))}
            </div>
          }
        >
          <Button size="small" variant="blue">
            {t('payout-reports.download-annual-report')}
          </Button>
        </Popover>
        {isPayoutsDeveloper && (
          <a
            href={
              isProdEnv
                ? `${OPS_TOOLS_PROD_URL}/venues/${venue.id}/payouts`
                : `${OPS_TOOLS_DEV_URL}/venues/${venue.id}/payouts`
            }
            target="_blank"
            rel="noreferrer"
            style={{ color: colors.wolt100, textDecoration: 'none' }}
          >
            <Button size="small" variant="blue" style={{ marginLeft: 4 }}>
              Open in opstools
            </Button>
          </a>
        )}
      </PageHeader>
      <SegmentedControlContainer>
        <SegmentedControl
          options={[
            {
              value: 'reports',
              label: t('navigation.payout-reports'),
            },
            {
              value: 'invoices',
              label: t('payout-reports.invoices'),
            },
          ]}
          onChange={value => handleReportOrInvoice(value)}
          value={reportsOrInvoices}
        />
      </SegmentedControlContainer>

      {reportsOrInvoices === 'reports' ? <PayoutReportList venue={venue} /> : <InvoiceList venueId={venue.id} />}

      <AnnualReportGenerationsModal
        onCloseClick={() => setAnnualReportDownloadStatus(AnnualReportDownloadStatus.Initial)}
        isOpen={!!annualReportDownloadStatus}
        status={annualReportDownloadStatus}
      />
    </Root>
  );
};

export default PayoutReportPage;
